import type { Device } from '@/api/deviceApi'
import { ORG_SERIAL_KEY_LABEL } from '@/stores/auth'
import type { Project, ProjectCounts } from '@/types/project'
import { resolveActionTypeLabel } from '@/utils/actionTypeUtils'

/**
 * Extracts the device identifier, preferring `action.device_id` over `id`.
 * Used across attach/reprovision forms for consistent device ID resolution.
 */
export function getDeviceId(d: Device): string {
	return (d.action?.device_id as string) ?? String(d.id)
}

const TERMINAL_ACTION_STATES = new Set(['completed', 'failed', 'marked_as_completed', 'timed_out_exhausted'])

/**
 * True when the device's latest action is an attach that hasn't reached a terminal state.
 * Devices whose attach failed/timed out stay attachable; only genuinely in-flight ones are blocked.
 */
export function hasInflightAttach(d: Device): boolean {
	if (!d.action) {
		return false
	}
	const resolvedType = resolveActionTypeLabel(d.action.type ?? '', d.action.reprovision_type?.reprovision_type)
	if (resolvedType !== 'attach') {
		return false
	}
	return !TERMINAL_ACTION_STATES.has((d.action.action_state ?? '').toLowerCase())
}

/**
 * Returns the label to use for device identifiers in a given project.
 *
 * - If the tenant is the root project, returns `'UID'`.
 * - If the project has a custom `serial_key` in its frontend settings, returns that.
 * - Otherwise falls back to the generic org-level label.
 */
export function getSerialKeyLabelForTenant(projects: Project[], tenantId?: string): string {
	if (!tenantId) {
		return ORG_SERIAL_KEY_LABEL
	}

	const project = projects.find(p => p.id === tenantId)
	if (!project) {
		return ORG_SERIAL_KEY_LABEL
	}

	if (project.is_org) {
		return 'UID'
	}

	const frontendSettings = (project as unknown as { frontend_settings?: { serial_key?: string } }).frontend_settings
	return frontendSettings?.serial_key || ORG_SERIAL_KEY_LABEL
}

type RootProjectLike = Pick<Project, 'id' | 'is_org'>

/** True for the organization-level (root) project. `is_org` is the only signal: the backend sets it on
 * exactly one tenant per org, and both `/v3/me` and the org tenant list always send it. */
export function isRootProjectRecord(record: { is_org?: boolean } | undefined | null): boolean {
	return record?.is_org === true
}

/** True when `projectId` is the organization-level (root) project. */
export function isRootProject(projectId: string | undefined, projects: RootProjectLike[]): boolean {
	if (!projectId) {
		return false
	}
	return isRootProjectRecord(projects.find(p => p.id === projectId))
}

/**
 * Separates root (organization-level) projects from the API response,
 * adjusting counts and pagination totals accordingly.
 */
export function separateRootProjects(
	data: Project[],
	allData: Project[],
	counts: ProjectCounts | null,
	paginationTotal: number,
): {
	data: Project[]
	allData: Project[]
	rootTenantId: string | null
	adjustedCounts: ProjectCounts
	adjustedTotal: number
} {
	const rootProject = allData.find(isRootProjectRecord)
	const rootCount = allData.filter(isRootProjectRecord).length

	const filteredData = data.filter(p => !isRootProjectRecord(p))

	const adjustedCounts: ProjectCounts = counts
		? {
				all: (counts.all ?? 0) - rootCount,
				active: (counts.active ?? 0) - (rootProject && rootProject.status === 'active' ? 1 : 0),
				deactivated: (counts.deactivated ?? 0) - (rootProject && rootProject.status === 'deactivated' ? 1 : 0),
			}
		: {
				all: filteredData.length,
				active: filteredData.filter(p => p.status === 'active').length,
				deactivated: filteredData.filter(p => p.status === 'deactivated').length,
			}

	return {
		data: filteredData,
		allData,
		rootTenantId: rootProject?.id ?? null,
		adjustedCounts,
		adjustedTotal: paginationTotal - rootCount,
	}
}
